import { authFetch } from '../auth/authFetch';
import { getAccessToken } from '../auth/authSession';
import { isJwtAuth } from '../auth/authMode';
import { UnauthorizedError, redirectToSignIn } from '../auth/identity';

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'
).replace(/\/$/, '');

function extractErrorMessage(errorPayload, status) {
  const fallback = `Request failed (${status})`;
  if (!errorPayload || typeof errorPayload !== 'object') {
    return fallback;
  }
  if (typeof errorPayload.message === 'string' && errorPayload.message.trim()) {
    return errorPayload.message;
  }
  if (typeof errorPayload.detail === 'string' && errorPayload.detail.trim()) {
    return errorPayload.detail;
  }
  return fallback;
}

function authHeaders() {
  if (!isJwtAuth()) {
    throw new UnauthorizedError('Org APIs require JWT auth mode');
  }
  const token = getAccessToken();
  if (!token) {
    throw new UnauthorizedError('Sign in required');
  }
  return { Authorization: `Bearer ${token}` };
}

async function parseResponse(response) {
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    try {
      const errorPayload = await response.json();
      message = extractErrorMessage(errorPayload, response.status);
    } catch {
      // keep default
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

async function request(path, { method = 'GET', body } = {}) {
  const response = await authFetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

export function listMembers() {
  return request('/v1/org/members');
}

export function updateMember(membershipId, body) {
  return request(`/v1/org/members/${membershipId}`, { method: 'PATCH', body });
}

export function listInvitations() {
  return request('/v1/org/invitations');
}

export function createInvitation(body) {
  return request('/v1/org/invitations', { method: 'POST', body });
}

export function revokeInvitation(invitationId) {
  return request(`/v1/org/invitations/${invitationId}`, { method: 'DELETE' });
}

export function resendInvitation(invitationId) {
  return request(`/v1/org/invitations/${invitationId}/resend`, { method: 'POST' });
}
